import { computed } from 'vue'
import { FindexClient } from '@/integrations/findex'
import type { UseAssetFormState, UseAssetFormComputedData } from '../types'
import { uniqueBy } from 'math'
import { commodityQuoteToOption } from '@/form/options/commodity'
import { pushToast } from 'components/toast/hooks'
import { defineField, Validators, type FormFieldDefinition } from '@/form/useForm'
import type { ICommodityQuote, ISavedDocument } from 'findef'
import { useApiHealth } from '@/modules/app/hooks/useApiHealth'

export const commodityQuoteField = (): FormFieldDefinition<UseAssetFormState, ICommodityQuote | ISavedDocument<ICommodityQuote, string>, UseAssetFormComputedData> => {
  const { isFmpAvailable } = useApiHealth()

  return defineField({
    label: 'Commodity Name',
    type: 'dbObject',
    component: 'select2',
    validators: [Validators.required()],
    required: true,
    //$validators: [validatorRequired()],
    placeholder: 'Start typing to search...',
		tooltip: 'Enter the name or ticker symbol for this commodity that follows the value  if the commody is listed on a market. We continuously expand available tickers',
    needs: ['isEditMode'],
    next: (state) => ({
      locked: state.isEditMode === true
    }),
    options: {
      toOption: commodityQuoteToOption,
      warning: computed(() =>
        isFmpAvailable.value === false ? 'Tickers are temporarily limited, contact team@myfindex.com for any questions' : undefined
      ),
      search: {
        api: {
          fetchAll: async () => {
            const [items] = await FindexClient.getCommodityQuotes({ limit: 100 })
            const options = uniqueBy(items || [], (it) => it.symbol).map((it) =>
              commodityQuoteToOption(it)
            )
            return options
          },
          fetch: async (queryString) => {
            const [items] = await FindexClient.getCommodityQuotes({ queryString })
            const options = uniqueBy(items || [], (it) => it.symbol).map((it) =>
              commodityQuoteToOption(it)
            )
            return options
          },
          fetchSuggestions: async () => {
            const [items] = await FindexClient.getCommodityQuotes({
              limit: 100,
              sort: { price: -1 }
            })
            const options = uniqueBy(items || [], (it) => it.symbol).map((it) =>
              commodityQuoteToOption(it)
            )
            return options
          }
        }
      },
      add: {
        invokeOnPressEnter: true,
        createItem: async (name) => {
          const [item] = await FindexClient.createCommodityQuote({
            name: name
          })

          if (!item) {
            pushToast({
              title: 'Error',
              subtitle: 'Failed to create commodity',
              variant: 'error'
            })
            return
          }

          return commodityQuoteToOption(item)
        }
      }
    },
    injectedProps: {
      icon: {
        src: async () => import('assets/img/svg/icons/search.svg?raw'),
        stroke: 'currentColor',
        size: '1rem'
      }
    }
  })
}
